import React from 'react';
import { NavLink } from 'react-router-dom';
import { BarChart3Icon, ShareIcon } from 'lucide-react';
import { brandModels } from '../data/mockData';
interface SidebarProps {
  isCampaign: boolean;
  activeBrand: string;
  setActiveBrand: (brand: string) => void;
  activeMarkets: string[];
  setActiveMarkets: React.Dispatch<React.SetStateAction<string[]>>;
  // Campaign filters
  activeModel?: string;
  setActiveModel?: (model: string) => void;
  activeType?: string | null;
  setActiveType?: (type: string | null) => void;
  activeYears?: string[];
  setActiveYears?: React.Dispatch<React.SetStateAction<string[]>>;
  // Social filters
  activeContentType?: string | null;
  setActiveContentType?: (type: string | null) => void;
  socialYears?: string[];
  setSocialYears?: React.Dispatch<React.SetStateAction<string[]>>;
}
export function Sidebar(props: SidebarProps) {
  const {
    isCampaign,
    activeBrand,
    setActiveBrand,
    activeMarkets,
    setActiveMarkets
  } = props;
  const toggleMultiSelect = (item: string, state: string[], setState: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (state.includes(item)) {
      setState(state.filter(i => i !== item));
    } else {
      setState([...state, item]);
    }
  };
  const handleBrandChange = (brand: string) => {
    setActiveBrand(brand);
    if (isCampaign) {
      const models = brandModels[brand];
      if (models && models.length > 0) {
        props.setActiveModel?.(models[0]);
      }
    }
  };
  const handleReset = () => {
    setActiveBrand('Toyota');
    setActiveMarkets([]);
    if (isCampaign) {
      props.setActiveModel?.('Yaris Cross');
      props.setActiveType?.('Tactical');
      props.setActiveYears?.([]);
    } else {
      props.setActiveContentType?.(null);
      props.setSocialYears?.(['2026']);
    }
  };
  const RequiredBadge = () => <span className="text-[10px] uppercase font-bold tracking-wider bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-md" data-id="element-111">
      Required
    </span>;
  const OptionalBadge = () => <span className="text-xs italic text-muted-foreground" data-id="element-112">Optional</span>;
  const availableModels = activeBrand ? brandModels[activeBrand] || [] : [];
  const markets = ['SLO', 'CRO', 'RS', 'BiH', 'MNE'];
  return <aside className="w-full lg:w-[320px] shrink-0" data-id="element-113">
      <div className="sticky top-6 bg-card rounded-2xl border border-border shadow-sm p-6 flex flex-col gap-6" data-id="element-114">
        {/* Navigation */}
        <div className="flex flex-col gap-2" data-id="element-115">
          <NavLink to="/campaign" className={({
          isActive
        }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${isActive || location.pathname === '/' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} data-id="element-116">
            <BarChart3Icon className="w-5 h-5" data-id="element-117" />
            Campaign Analytics
          </NavLink>
          <NavLink to="/social" className={({
          isActive
        }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} data-id="element-118">
            <ShareIcon className="w-5 h-5" data-id="element-119" />
            Social Analytics
          </NavLink>
        </div>

        <div className="h-px bg-border w-full" data-id="element-120" />

        {/* Header */}
        <div className="flex items-center justify-between pb-2" data-id="element-121">
          <h2 className="text-xl font-bold text-foreground" data-id="element-122">Filtri</h2>
          <button onClick={handleReset} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-id="element-123">
            Reset
          </button>
        </div>

        {/* 1. Brand (Shared) */}
        <div className="flex flex-col gap-3" data-id="element-124">
          <div className="flex items-center justify-between" data-id="element-125">
            <span className="text-sm font-semibold text-foreground" data-id="element-126">Brand</span>
            <RequiredBadge data-id="element-127" />
          </div>
          <div className="flex gap-2" data-id="element-128">
            {['Toyota', 'Lexus'].map(brand => <button key={brand} onClick={() => handleBrandChange(brand)} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${activeBrand === brand ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-129">
                {brand}
              </button>)}
          </div>
        </div>

        {/* 2. Market (Shared) */}
        <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-130">
          <div className="flex items-center justify-between" data-id="element-131">
            <span className="text-sm font-semibold text-foreground" data-id="element-132">
              Market
            </span>
            <OptionalBadge data-id="element-133" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-134">
            <button onClick={() => {
            if (activeMarkets.length === markets.length) {
              setActiveMarkets([]);
            } else {
              setActiveMarkets(markets);
            }
          }} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeMarkets.length === markets.length ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-135">
              All
            </button>
            {markets.map(market => <button key={market} onClick={() => toggleMultiSelect(market, activeMarkets, setActiveMarkets)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeMarkets.includes(market) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-136">
                {market}
              </button>)}
          </div>
        </div>

        {isCampaign ? <>
            {/* Model */}
            <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-137">
              <div className="flex items-center justify-between" data-id="element-138">
                <span className="text-sm font-semibold text-foreground" data-id="element-139">
                  Model
                </span>
                <RequiredBadge data-id="element-140" />
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-141">
                {availableModels.map(model => <button key={model} onClick={() => props.setActiveModel?.(model)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${props.activeModel === model ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-142">
                    {model}
                  </button>)}
              </div>
            </div>

            {/* Campaign Type */}
            <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-143">
              <div className="flex items-center justify-between" data-id="element-144">
                <span className="text-sm font-semibold text-foreground" data-id="element-145">
                  Campaign type
                </span>
                <RequiredBadge data-id="element-146" />
              </div>
              <div className="flex gap-2" data-id="element-147">
                <button onClick={() => props.setActiveType?.(props.activeType === 'Brand' ? null : 'Brand')} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${props.activeType === 'Brand' ? 'bg-slate-800 text-white shadow-md' : 'bg-slate-800/10 text-slate-800 hover:bg-slate-800/20'}`} data-id="element-148">
                  Brand
                </button>
                <button onClick={() => props.setActiveType?.(props.activeType === 'Tactical' ? null : 'Tactical')} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${props.activeType === 'Tactical' ? 'bg-red-500 text-white shadow-md' : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'}`} data-id="element-149">
                  Tactical
                </button>
              </div>
            </div>

            {/* Year */}
            <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-150">
              <div className="flex items-center justify-between" data-id="element-151">
                <span className="text-sm font-semibold text-foreground" data-id="element-152">
                  Year
                </span>
                <OptionalBadge data-id="element-153" />
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-154">
                {['2023', '2024', '2025', '2026'].map(year => <button key={year} onClick={() => toggleMultiSelect(year, props.activeYears || [], props.setActiveYears!)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${(props.activeYears || []).includes(year) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-155">
                    {year}
                  </button>)}
              </div>
            </div>
          </> : <>
            {/* Content Type */}
            <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-156">
              <div className="flex items-center justify-between" data-id="element-157">
                <span className="text-sm font-semibold text-foreground" data-id="element-158">
                  Content type
                </span>
                <OptionalBadge data-id="element-159" />
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-160">
                {['Brand', 'Tactical', 'Value chain'].map(type => <button key={type} onClick={() => props.setActiveContentType?.(props.activeContentType === type ? null : type)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${props.activeContentType === type ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-161">
                    {type}
                  </button>)}
              </div>
            </div>

            {/* Year */}
            <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-162">
              <div className="flex items-center justify-between" data-id="element-163">
                <span className="text-sm font-semibold text-foreground" data-id="element-164">
                  Year
                </span>
                <OptionalBadge data-id="element-165" />
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-166">
                {['2023', '2024', '2025', '2026'].map(year => <button key={year} onClick={() => toggleMultiSelect(year, props.socialYears || [], props.setSocialYears!)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${(props.socialYears || []).includes(year) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-167">
                    {year}
                  </button>)}
              </div>
            </div>
          </>}
      </div>
    </aside>;
}